// Shared Preact + htm bindings for all migrated screen components (DRY: ONE htm.bind, ONE
// place to add cross-component helpers). Every component under client/components/ imports
// { html } from here instead of re-binding htm — see PREACT-MIGRATION.md. Components are
// PURE functions of the global `app` state (passed in as a prop each mount) and emit the
// SAME ids / class names / data-action attributes / visible text as the classic markup, so
// the existing document-level click delegation keeps handling clicks (no per-component
// onClick) and the smoke grep contract stays green.
import { h } from 'preact';
import type { VNode } from 'preact';
import htm from 'htm';

export { h };

/**
 * The single `htm.bind(h)` tagged-template the whole client shares. Used as a template literal
 * (html`...`), it returns a Preact VNode — or an array of VNodes when the template has sibling
 * roots / a fragment. Typed explicitly so consumers see a precise Preact type, not htm's any.
 */
export const html: (strings: TemplateStringsArray, ...values: unknown[]) => VNode | VNode[] =
  htm.bind(h);

// classNames(...) — tiny conditional class joiner so components stay declarative
// (e.g. on/off toggles) without bespoke string concatenation in each file. Parts are usually
// `string | false` (e.g. `cond && 'on'`); any falsy part (false/null/undefined/'') is dropped.
export const cx = (...parts: (string | false | null | undefined)[]): string =>
  parts.filter(Boolean).join(' ');

// i18n: the single translator, shared by every component. Delegates to the classic script's window.__t
// (which owns window.STR + app.lang — one source, synchronously available to both render paths). Falls
// back to the key if the classic layer isn't up yet (never throws). t('hand.yourWord') / t('k',{n:3}).
export const t = (key: string, vars?: Record<string, string | number>): string => {
  const f = (globalThis as any).__t;
  return typeof f === 'function' ? f(key, vars) : key;
};

/** Inside the Bluffo iOS app (ios/: a WKWebView that defines window.__bluffoApp before the page runs). The app is for
 *  players (design/IOS-APP.md): Play now comes first, and there's no "send your agent" and no Google sign-in. */
export const isApp = (): boolean => Boolean((globalThis as any).__bluffoApp);

// ---------------------------------------------------------------------------
// Tiny cross-component formatting helpers — pulled up from the overlay/scoreboard screens that
// each re-declared them (DRY). Each is a PURE string/VNode producer that emits BYTE-IDENTICAL
// output to the inline expression it replaces, so the rendered DOM (class names / visible text)
// is unchanged and the click-delegation + CDP harness + smoke grep contracts stay green.
// ---------------------------------------------------------------------------

/** Podium medals for the top-3 rows (Leaderboard / Standings / Scoreboard ranks). */
export const MEDALS: string[] = ['🥇', '🥈', '🥉'];

/** Rank badge for row index `i`: a medal for the top 3, otherwise the 1-based position number. */
export const rankBadge = (i: number): string | number => MEDALS[i] || i + 1;

/** A score with an explicit leading '+' when positive (e.g. 3 -> '+3', -2 -> '-2', 0 -> '0'). */
export const signed = (n: number): string => (n > 0 ? '+' : '') + n;

/** Avatar emoji with the standard fallback: AI -> 🤖, else the player's emoji or 🧑. The param is
 *  structural (Player / Standing / a browse avatar pip all satisfy it); `ai` is absent on the
 *  score-row shapes, so it reads falsy and the call collapses to `emoji || '🧑'` — byte-identical. */
export const glyph = (p: { ai?: unknown; emoji?: string }): string =>
  p.ai ? '🤖' : (p.emoji || '🧑');

/** The remembered nickname from localStorage (au-name), guarded for SSR / privacy-mode throws.
 *  Used by <Home> + <JoinLanding> to seed an UNCONTROLLED name input's defaultValue. */
export const rememberedName = (): string => {
  try {
    return (typeof localStorage !== 'undefined' && localStorage.getItem('au-name')) || '';
  } catch {
    return '';
  }
};

// ---------------------------------------------------------------------------
// Shared overlay scaffolding — the .overlay/.sheet + winner-banner skeletons the 5 modal screens
// (Rules / Standings / Leaderboard / Invite / Scoreboard) all repeat. These are htm child slots:
// pass the differing pieces (banner sub/size, footer close-row action, body) as props so each
// screen's emitted markup is identical to its old hand-rolled string.
// ---------------------------------------------------------------------------

/** htm child(ren): the value type Preact accepts as a slot — a vnode, a list, a string, or empty. */
type Slot = VNode | VNode[] | string;

interface OverlaySheetProps {
  /** data-action of the default footer close-row's button (e.g. 'close-rules'); omit when passing
   *  a custom `footer`. The default close-row matches Standings/Leaderboard exactly. */
  close?: string;
  /** a custom footer (Invite's copy+close row, Scoreboard's recap+rematch row); overrides `close`. */
  footer?: Slot;
  /** data-action of the corner ✕ (defaults to `close`). Every sheet should have one — Esc and a backdrop tap
   *  click it too (index.html), so a sheet with neither ✕ nor close button can't be left. */
  x?: string;
  children?: Slot;
}

/** The `<div class="overlay"><div class="sheet">…</div></div>` scaffold + a standard footer
 *  close-row. Emits exactly the markup the overlay screens used to inline. */
export function OverlaySheet({ close, footer, x, children }: OverlaySheetProps) {
  const foot = footer ?? (close
    ? html`<div class="row" style="margin-top:12px"><button class="yellow" data-action=${close}>${t('common.close')}</button></div>`
    : '');
  const xa = x || close;
  return html`<div class="overlay"><div class="sheet">${xa ? html`<button class="sheet-x" data-action=${xa} aria-label=${t('common.close')}>✕</button>` : ''}${children}${foot}</div></div>`;
}

interface BannerProps {
  /** the big headline (already includes its leading emoji, e.g. '🏆 排行榜'). */
  title: Slot;
  /** the muted sub-line; omitted (no .sub div emitted) when falsy. */
  sub?: Slot;
  /** the .big font-size (an existing clamp() string), inlined verbatim as `font-size:${size}`. */
  size: string;
  children?: Slot;
}

/** The `.winner-banner` header (big headline + optional .sub) the Rules/Standings/Leaderboard
 *  screens repeat. Scoreboard's banner is intentionally NOT routed through here — it puts a .sfx
 *  line BEFORE an unstyled .big, so it wouldn't stay byte-identical. */
export function Banner({ title, sub, size, children }: BannerProps) {
  return html`<div class="winner-banner"><div class="big" style="font-size:${size}">${title}</div>${sub ? html`<div class="sub">${sub}</div>` : ''}${children ?? ''}</div>`;
}
